<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <ComponentHeader
      title="DataSources"
      subtitle="The databases a data attribute can read from. Kairon EDW is the default and needs no connection."
    >
      <button class="btn-secondary-sm" :disabled="loading" @click="load">
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" :spin="loading" />
        Refresh
      </button>
      <button class="btn-accent-sm ml-2" @click="addVisible = true">
        <font-awesome-icon :icon="['fas', 'database']" />
        Add Data Connection
      </button>
    </ComponentHeader>

    <p v-if="error" class="px-4 py-3 text-xs text-red-dark">{{ error }}</p>
    <div v-else class="overflow-x-auto">
      <table class="w-full text-xs">
        <thead>
          <tr class="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
            <th class="px-4 py-2 font-semibold">Name</th>
            <th class="px-4 py-2 font-semibold">Kind</th>
            <th class="px-4 py-2 font-semibold">Account or host</th>
            <th class="px-4 py-2 font-semibold">Database</th>
            <th class="px-4 py-2 font-semibold text-right">DataAttributes</th>
            <th class="px-4 py-2 font-semibold">Last test</th>
            <th class="px-4 py-2 font-semibold">Created</th>
            <th class="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody>
          <tr class="border-b border-gray-100 text-gray-500">
            <td class="px-4 py-2 text-gray-800">Kairon EDW</td>
            <td class="px-4 py-2">Kairon Snowflake</td>
            <td class="px-4 py-2" colspan="6">Default for every data attribute with no connection set.</td>
          </tr>
          <tr v-if="loading && !sources.length">
            <td class="px-4 py-3 text-xs text-gray-500" colspan="8">Loading…</td>
          </tr>
          <tr v-for="s in sources" :key="s.id" class="border-b border-gray-100 hover:bg-gray-50">
            <td class="px-4 py-2 text-gray-800">
              <span>{{ s.name }}</span>
              <span v-if="s.is_system" class="ml-1 text-xs text-gray-500">system</span>
              <span v-if="!s.is_active" class="ml-1 text-xs text-gray-500">inactive</span>
            </td>
            <td class="px-4 py-2 text-gray-600">{{ kindLabel(s) }}</td>
            <td class="px-4 py-2 text-gray-600 font-mono text-xs truncate max-w-xs">{{ s.host || s.account || '—' }}</td>
            <td class="px-4 py-2 text-gray-600 font-mono text-xs">{{ s.database || '—' }}</td>
            <td class="px-4 py-2 text-right tabular-nums">{{ s.data_attribute_count ?? '—' }}</td>
            <td class="px-4 py-2">
              <span :class="statusClass(s.test_status)">{{ s.test_status || 'untested' }}</span>
              <span v-if="s.last_tested_at" class="ml-1 text-xs text-gray-500">{{ fmtDate(s.last_tested_at) }}</span>
            </td>
            <td class="px-4 py-2 text-xs text-gray-500">{{ fmtDate(s.created_at) }}<template v-if="s.created_by_name"> · <span>{{ s.created_by_name }}</span></template></td>
            <td class="px-4 py-2 text-right whitespace-nowrap">
              <button class="ds-act" :disabled="testing === s.id" @click="test(s)">
                {{ testing === s.id ? 'Testing…' : 'Test' }}
              </button>
              <button
                v-if="!s.is_system"
                class="ds-act ml-3 text-red-dark"
                :disabled="!!s.data_attribute_count"
                :title="s.data_attribute_count ? 'Data attributes still read from this connection' : 'Delete this connection'"
                @click="remove(s)"
              >
                Delete
              </button>
            </td>
          </tr>
          <tr v-if="!loading && !sources.length">
            <td class="px-4 py-3 text-xs text-gray-500" colspan="8">No connections yet.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="notice" class="px-4 py-2 text-xs text-gray-600 border-t border-gray-100">{{ notice }}</p>

    <AddDataConnectionModal
      :visible="addVisible"
      @close="addVisible = false"
      @created="load"
    />
  </div>
</template>

<script>
import axios from 'axios';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import AddDataConnectionModal from './AddDataConnectionModal.vue';

const SOURCE_BASE = '/api/impact_analytics/data-sources/';
const KIND_LABELS = {
  snowflake: 'Customer Snowflake',
  jdbc_odbc: 'JDBC / ODBC',
  'kairon-snowflake': 'Kairon Snowflake',
  'kairon-postgres': 'Kairon Postgres',
};
const detail = (e, fallback) => e?.response?.data?.detail || (Array.isArray(e?.response?.data) && e.response.data[0]) || fallback;

export default {
  name: 'OverviewEtlDataSources',
  components: { ComponentHeader, AddDataConnectionModal },
  data() {
    return { sources: [], loading: false, error: '', notice: '', testing: null, addVisible: false };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get(SOURCE_BASE);
        this.sources = Array.isArray(data) ? data : (data.results || []);
      } catch (e) {
        this.error = detail(e, 'Could not load data connections.');
      } finally {
        this.loading = false;
      }
    },
    async test(s) {
      this.testing = s.id;
      this.notice = '';
      try {
        const { data } = await axios.post(`${SOURCE_BASE}${s.id}/test/`);
        this.notice = `${s.name}: ${data.success ? 'connected' : (data.message || 'failed')}`;
      } catch (e) {
        this.notice = `${s.name}: ${detail(e, 'test failed')}`;
      } finally {
        this.testing = null;
        await this.load();
      }
    },
    async remove(s) {
      if (!window.confirm('Delete this connection? Its stored credentials are deleted too.')) return;
      this.notice = '';
      try {
        await axios.delete(`${SOURCE_BASE}${s.id}/`);
        this.notice = `Deleted ${s.name}.`;
      } catch (e) {
        this.notice = `${s.name}: ${detail(e, 'delete failed')}`;
      }
      await this.load();
    },
    kindLabel(s) {
      if (s.connection_kind === 'jdbc_odbc' && s.protocol) return s.protocol.toUpperCase();
      return KIND_LABELS[s.connection_kind] || s.connection_kind || '—';
    },
    statusClass(status) {
      if (status === 'success') return 'text-green-dark';
      if (status === 'failed') return 'text-red-dark';
      return 'text-gray-500';
    },
    fmtDate(iso) {
      return iso ? new Date(iso).toLocaleDateString() : '';
    },
  },
};
</script>

<style scoped>
.ds-act { font-size: 12px; font-weight: 500; color: #4C6E97; background: none; border: 0; padding: 0; cursor: pointer; }
.ds-act:hover { text-decoration: underline; }
.ds-act:disabled { color: #9ca3af; cursor: default; text-decoration: none; }
.ds-act.text-red-dark:not(:disabled) { color: #D53D3D; }
</style>
